// v2.17.0 — Generic Directory Card : carte unifiée pour Administration & Finance.
// Mêmes dimensions que DivertissementCard/EmploiCard (240×~316). Slots dynamiques.

import { useState } from "react";
import { toast } from "sonner";
import FavoriteStarButton from "./FavoriteStarButton";

const TRADE_LABELS = [
  [/pharmacie/i, "Voir la pharmacie"],
  [/cabinet|m[ée]dical|sant[ée]|clinique|m[ée]decine/i, "Voir le cabinet"],
  [/justice|avocat|notaire|juridique|huissier/i, "Voir le cabinet"],
  [/restaurant|restauration|maquis/i, "Voir le restaurant"],
  [/h[ôo]tel|r[ée]sidence/i, "Voir l'hôtel"],
  [/garage|m[ée]canique|auto/i, "Voir le garage"],
  [/couture|tailleur|artisan|atelier|menuiserie/i, "Voir l'atelier"],
  [/pressing/i, "Voir le pressing"],
  [/salon|coiffure|beaut[ée]|esth[ée]tique/i, "Voir le salon"],
  [/boucherie/i, "Voir la boucherie"],
  [/boulangerie/i, "Voir la boulangerie"],
  [/agence|finance|banque|assurance/i, "Voir l'agence"],
  [/pr[êe]t[- ][àa][- ]porter|boutique|fleur|shopping|magasin|commerce|alimentation|[ée]lectronique|d[ée]co/i, "Voir la boutique"],
];

// v3.13.0 — Libellé du bouton principal adapté au métier (jamais "boutique"
// pour un médecin : cabinet, atelier, salon, agence…).
function tradeLabel(item) {
  const hay = `${item.category_label || ""} ${item.category || ""} ${item.category_id || ""} ${item.tagline || ""}`;
  for (const [re, label] of TRADE_LABELS) if (re.test(hay)) return label;
  return "Voir la fiche";
}

export default function GenericDirectoryCard({
  item,
  accent = "#3B82F6",
  testIdPrefix,           // ex: "admin-card" | "finance-card"
  detailRoute,            // ex: "/admin" | "/finance"
  // Mapping pour extraire les champs (variations admin/finance).
  titleField = "name",    // "service_name" pour admin
  subtitleField = "type", // "organism" pour admin
  priceField = null,      // "cost_display" pour admin, "rate_display" pour finance
  badgeField = null,      // ex: online flag pour admin
  favItemType = "directory", // v2.19.22f-2 : type passé au bouton favoris
}) {
  const [hov, setHov] = useState(false);
  if (!item) return null;
  const title = item[titleField] || item.name || item.service_name || "—";
  const subtitle = item[subtitleField] || "";
  const priceLabel = priceField ? item[priceField] : null;
  const badge = badgeField ? item[badgeField] : null;

  const openSite = () => {
    if (!item.website) { toast.error("Site web indisponible"); return; }
    window.open(item.website, "_blank", "noopener,noreferrer");
  };
  const openOnline = () => {
    if (!item.online_url) return;
    window.open(item.online_url, "_blank", "noopener,noreferrer");
  };
  const call = () => {
    if (!item.phone) { toast.error("Numéro indisponible"); return; }
    window.location.href = `tel:${item.phone.replace(/\s+/g, "")}`;
  };
  const copy = async () => {
    const text = `${title}\n${subtitle}\n📍 ${item.location_text || ''}\n📞 ${item.phone || ''}\n🌐 ${item.website || '—'}`;
    try { await navigator.clipboard.writeText(text); toast.success("📋 Copié"); }
    catch (_) { toast.error("Copie impossible"); }
  };
  const share = () => {
    const msg = `📋 ${title}\n${subtitle}\n📍 ${item.location_text || ''}\n📞 ${item.phone || ''}\n${item.website ? '🌐 ' + item.website : ''}\n\nVu sur AYA 🇨🇮`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const goDetail = () => window.open(`${detailRoute}/${item.id}`, "_blank");

  return (
    <article
      data-testid={`${testIdPrefix}-${item.id}`}
      className="civ-mode-card"
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        position: "relative", borderRadius: 14, overflow: "hidden",
        background: "#0f172a",
        border: `1px solid ${hov ? accent : "rgba(255,255,255,0.08)"}`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.18), 0 6px 14px rgba(0,0,0,0.22)",
        display: "flex", flexDirection: "column",
        transition: "transform 200ms ease, border-color 200ms ease",
        transform: hov ? "translateY(-2px)" : "translateY(0)",
        flex: "0 0 240px", width: 240, scrollSnapAlign: "start",
      }}
    >
      <FavoriteStarButton
        itemType={favItemType}
        itemId={item.id}
        itemLabel={title}
        itemMeta={{ category: item.category_id || item.category, commune: item.commune || item.commune_name }}
      />
      <div style={{
        position: "relative", height: 110,
        background: `linear-gradient(135deg, ${accent}E6, ${accent}66)`,
        display: "flex", flexDirection: "column", justifyContent: "space-between", padding: 10,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 6 }}>
          <span style={{
            background: "rgba(15,23,42,0.85)", color: "#fff",
            padding: "3px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700,
          }}>{item.category_emoji} {(item.category_label || "").slice(0, 18)}</span>
          {item.verified && (
            <span style={{ background: "#fff", color: accent, padding: "3px 8px", borderRadius: 999, fontSize: 10, fontWeight: 800 }}>✓ Vérifiée</span>
          )}
        </div>
        {item.online_available && (
          <span style={{
            background: "#fff", color: accent, padding: "3px 8px", borderRadius: 999,
            fontSize: 10, fontWeight: 700, alignSelf: "flex-start",
          }}>🌐 Dispo en ligne</span>
        )}
      </div>

      <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: "#fff", lineHeight: 1.25 }}>{title}</div>
        {subtitle && <div style={{ fontSize: 11.5, color: "rgba(255,255,255,0.65)" }}>{subtitle}</div>}
        {Number(item.rating) > 0 && (
          <div style={{ fontSize: 12, color: "#34D399", fontWeight: 700 }}>
            ★ {Number(item.rating).toFixed(1)}{item.reviews_count ? <span style={{ color: "rgba(255,255,255,0.5)", fontWeight: 400 }}> ({item.reviews_count})</span> : null}
          </div>
        )}
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)" }}>📍 {item.location_text || item.commune_name}</div>
        {priceLabel && <div style={{ fontSize: 11.5, color: accent, fontWeight: 700, marginTop: 2 }}>💰 {priceLabel}</div>}
        {item.duration && <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)" }}>⏱ {item.duration}</div>}

        <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
          {item.website && (
            <button onClick={openSite} data-testid={`${testIdPrefix}-site-btn`} style={btnPrimary(accent)}>🌐 Site</button>
          )}
          {item.online_url && (
            <button onClick={openOnline} data-testid={`${testIdPrefix}-online-btn`} style={btnPrimary(accent)}>📲 En ligne</button>
          )}
          {item.phone && (
            <button onClick={call} data-testid={`${testIdPrefix}-call-btn`} style={btnCall}>📞</button>
          )}
          <button onClick={copy} data-testid={`${testIdPrefix}-copy-btn`} style={btnSec}>📋</button>
          <button onClick={share} data-testid={`${testIdPrefix}-share-btn`} style={btnWA}>📤</button>
        </div>

        <div style={{ marginTop: 6 }}>
          <button onClick={goDetail} data-testid={`${testIdPrefix}-detail-btn`} style={{
            width: "100%", background: "linear-gradient(135deg,#EFC75E,#D9A83C)", color: "#1A1206",
            border: "none", padding: "8px 10px", borderRadius: 10,
            fontSize: 12, fontWeight: 800, cursor: "pointer",
          }}>{tradeLabel(item)}</button>
        </div>
      </div>
    </article>
  );
}

const btnPrimary = (a) => ({
  background: a, color: "#fff", border: "none",
  padding: "5px 10px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer",
});
const btnCall = { background: "#16A34A", color: "#fff", border: "none", padding: "5px 8px", borderRadius: 8, fontSize: 11.5, cursor: "pointer", fontWeight: 700 };
const btnSec = { background: "rgba(255,255,255,0.06)", color: "#CBD5E1", border: "1px solid rgba(255,255,255,0.18)", padding: "5px 8px", borderRadius: 8, fontSize: 11.5, cursor: "pointer" };
const btnWA = { background: "#25D366", color: "#fff", border: "none", padding: "5px 8px", borderRadius: 8, fontSize: 11.5, cursor: "pointer" };
